import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { toast } from 'react-toastify';
import './AvailabilityCalendar.css';

const AvailabilityCalendar = ({ teacherId, initialAvailability = {} }) => {
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(false);

  const daysOfWeek = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' }
  ];

  // 07:00'dan 22:00'a kadar yarım saatlik dilimler
  const timeSlots = [];
  for (let hour = 7; hour <= 22; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
  }

  useEffect(() => {
    // İlk yükleme - eğer boşsa her gün için boş array oluştur
    const initialData = {};
    daysOfWeek.forEach(day => {
      initialData[day.key] = initialAvailability[day.key] || [];
    });
    setAvailability(initialData);
  }, [initialAvailability]);

  const toggleTimeSlot = (day, time) => {
    setAvailability(prev => {
      const daySlots = prev[day] || [];
      const newDaySlots = daySlots.includes(time)
        ? daySlots.filter(t => t !== time)
        : [...daySlots, time].sort();
      
      return {
        ...prev,
        [day]: newDaySlots
      };
    });
  };

  const selectAllDay = (day) => {
    setAvailability(prev => ({
      ...prev,
      [day]: [...timeSlots]
    }));
  };

  const clearDay = (day) => {
    setAvailability(prev => ({
      ...prev,
      [day]: []
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'teachers', teacherId), {
        availability: availability,
        updatedAt: new Date().toISOString()
      });
      toast.success('Availability updated successfully!');
    } catch (error) {
      console.error('Save availability error:', error);
      toast.error('Failed to save availability');
    } finally {
      setLoading(false);
    }
  };

  const isTimeSlotSelected = (day, time) => {
    return availability[day]?.includes(time) || false;
  };

  const getSelectedSlotsCount = (day) => {
    return availability[day]?.length || 0;
  };

  return (
    <div className="availability-calendar">
      <div className="calendar-header">
        <h2>Set Your Available Hours</h2>
        <p>Click on time slots to mark when you're available for lessons</p>
      </div>

      <div className="calendar-grid">
        {daysOfWeek.map(day => (
          <div key={day.key} className="day-column">
            <div className="day-header">
              <div className="day-info">
                <h3>{day.label}</h3>
                <span className="slots-count">
                  {getSelectedSlotsCount(day.key)} slots selected
                </span>
              </div>
              <div className="day-actions">
                <button
                  type="button"
                  onClick={() => selectAllDay(day.key)}
                  className="btn-small btn-secondary"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={() => clearDay(day.key)}
                  className="btn-small btn-outline"
                >
                  Clear
                </button>
              </div>
            </div>

            <div className="time-slots">
              {timeSlots.map(time => (
                <button
                  key={time}
                  type="button"
                  onClick={() => toggleTimeSlot(day.key, time)}
                  className={`time-slot ${isTimeSlotSelected(day.key, time) ? 'selected' : ''}`}
                >
                  {time}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="calendar-footer">
        <div className="footer-info">
          <div className="legend">
            <span className="legend-item">
              <span className="legend-box selected"></span>
              Available
            </span>
            <span className="legend-item">
              <span className="legend-box"></span>
              Not Available
            </span>
          </div>
          <p className="help-text">
            ℹ️ Students will be able to book lessons during your available hours
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          className="btn btn-primary btn-lg"
          disabled={loading}
        >
          {loading ? <div className="spinner"></div> : '💾 Save Availability'}
        </button>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
